import { SerializedStyles, css } from '@emotion/react'
import { EffectPlayback, TransitionType } from './FullscreenImage'

export type EffectStrength = 'default' | 'subtle' | 'strong'

const zoomScaleFactor = (strength: EffectStrength): number => {
  switch (strength) {
  case 'subtle':
    return 0.3
  case 'strong':
    return 1.0
  case 'default':
    return 0.8
  }
}

export type ZoomType = 'zoomIn' | 'zoomOut'
export type ZoomDirection =
  | 'top-left'
  | 'top-right'
  | 'center'
  | 'bottom-left'
  | 'bottom-right'
export const zoomEffect = (
  type: ZoomType,
  direction: ZoomDirection,
  playback: EffectPlayback,
  strength: EffectStrength,
  scrollPercentage: number
): SerializedStyles => {
  // 'top-left' -> 'top left'
  const directionWithSpaces = direction?.replace('-', ' ')
  const transformDirection = css`
    -webkit-transform-origin: ${directionWithSpaces};
    -moz-transform-origin: ${directionWithSpaces};
    -ms-transform-origin: ${directionWithSpaces};
    -o-transform-origin: ${directionWithSpaces};
    transform-origin: ${directionWithSpaces};
  `

  const scaleFactor = zoomScaleFactor(strength)

  if (playback === 'scroll') {
    const scrollPercentageWithMargins =
      applyScrollPercentageMargins(scrollPercentage)
    let currentScaleFactor: number
    if (type === 'zoomIn') {
      // project scroll percentage to a range between 1 to scaleFactor
      currentScaleFactor = 1 + scaleFactor * scrollPercentageWithMargins
    } else {
      // project scroll percentage to a range between scaleFactor to 1
      currentScaleFactor = 1 + scaleFactor * (1 - scrollPercentageWithMargins)
    }

    return css`
      ${transformDirection}
      will-change: transform;
      transform: scale(${currentScaleFactor});
      -ms-transform: scale(${currentScaleFactor});
      -webkit-transform: scale(${currentScaleFactor});
      -o-transform: scale(${currentScaleFactor});
      -moz-transform: scale(${currentScaleFactor});
    `
  } else {
    return css`
      animation: move-${direction} 40s linear infinite alternate;
      -ms-animation: move-${direction} 40s linear infinite alternate;
      -webkit-animation: move-${direction} 40s linear infinite alternate;
      -o-animation: move-${direction} 40s linear infinite alternate;
      -moz-animation: move-${direction} 40s linear infinite alternate;
      @-webkit-keyframes move-${direction} {
        0% {
          ${transformDirection}
          transform: scale(1);
          -webkit-transform: scale(1);
          -o-transform: scale(1);
          -moz-transform: scale(1);
        }
        100% {
          ${transformDirection}
          transform: scale(${1 + scaleFactor});
          -ms-transform: scale(${1 + scaleFactor});
          -webkit-transform: scale(${1 + scaleFactor});
          -o-transform: scale(${1 + scaleFactor});
          -moz-transform: scale(${1 + scaleFactor});
        }
      }
    `
  }
}

const moveTranslateFactor = (
  strength: EffectStrength,
  scrollPercentage: number
): number => {
  switch (strength) {
  case 'subtle':
    // project percentage to range of -5% to 5%
    return scrollPercentage * 10 - 5
  case 'strong':
    // project percentage to range of -12% to 12%
    return scrollPercentage * 24 - 12
  case 'default':
    // project percentage to range of -10% to 10%
    return scrollPercentage * 20 - 10
  }
}

const moveTranslatePercent = (strength: EffectStrength): string => {
  switch (strength) {
  case 'subtle':
    return '5'
  case 'strong':
    return '12'
  case 'default':
    return '10'
  }
}

export type MoveDirection = 'up' | 'down' | 'left' | 'right'
export const moveEffect = (
  type: MoveDirection,
  playback: EffectPlayback,
  strength: EffectStrength,
  scrollPercentage: number
): SerializedStyles => {
  if (playback === 'scroll') {
    const scrollPercentageWithMargins =
      applyScrollPercentageMargins(scrollPercentage)
    let translate: string
    if (type === 'up') {
      translate = `translateY(${moveTranslateFactor(strength, scrollPercentageWithMargins)}%);`
    } else if (type === 'down') {
      translate = `translateY(${moveTranslateFactor(strength, 1 - scrollPercentageWithMargins)}%);`
    } else if (type === 'left') {
      translate = `translateX(${moveTranslateFactor(strength, scrollPercentageWithMargins)}%);`
    } else {
      translate = `translateX(${moveTranslateFactor(strength, 1 - scrollPercentageWithMargins)}%);`
    }

    return css`
      will-change: transform;
      transform: scale(1.4) ${translate};
      transform-origin: center;
    `
  } else {
    let translateFrom: string
    let translateTo: string
    if (type === 'up') {
      translateFrom = `translateY(-${moveTranslatePercent(strength)}%)`
      translateTo = `translateY(${moveTranslatePercent(strength)}%)`
    } else if (type === 'down') {
      translateFrom = `translateY(${moveTranslatePercent(strength)}%)`
      translateTo = `translateY(-${moveTranslatePercent(strength)}%)`
    } else if (type === 'left') {
      translateFrom = `translateX(-${moveTranslatePercent(strength)}%)`
      translateTo = `translateX(${moveTranslatePercent(strength)}%)`
    } else {
      translateFrom = `translateX(${moveTranslatePercent(strength)}%)`
      translateTo = `translateX(-${moveTranslatePercent(strength)}%)`
    }

    return css`
      transform: scale(1.4) ${translateFrom};
      transform-origin: center;
      animation: scrollImage 20s forwards;

      animation-name: scrollImage-${type};
      @keyframes scrollImage-${type} {
        from {
          transform: scale(1.4) ${translateFrom};
        }
        to {
          transform: scale(1.4) ${translateTo};
        }
      }
    `
  }
}

export const transitionEffect = (
  transitionType: TransitionType,
  scrollPercentage: number
): SerializedStyles => {
  let transitionEffect: string
  if (transitionType === 'wipe_right-to-left') {
    transitionEffect = `inset(0 ${scrollPercentage * 100}% 0 0)`
  } else {
    transitionEffect = `inset(0 0 0 ${scrollPercentage * 100}%)`
  }

  return css`
    will-change: clip-path;
    clip-path: ${transitionEffect};
  `
}

/*
 * Some animations (on_scroll) should not immediately start when coming into view, since
 * this might result in a direct "jump".
 * Apply a percentage margin to the scroll percentage, so that it starts at 20% and stops at 80%
 */
const applyScrollPercentageMargins = (scrollPercentage: number): number => {
  const percentageMargin = 0.2

  if (scrollPercentage < percentageMargin) {
    return 0
  } else if (scrollPercentage > 1 - percentageMargin) {
    return 1
  } else {
    return (scrollPercentage - percentageMargin) / (1 - 2 * percentageMargin)
  }
}
